import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Copy } from 'lucide-react';
import { useId, useState } from 'react';

import { Botao } from '@/componentes/Botao';
import { Campo, CampoSelecao, CampoTexto } from '@/componentes/Campo';
import { Janela } from '@/componentes/JanelaDeAcao';
import { formatarDecimal, interpretarNumero, paraCampoDataHora } from '@/lib/formato';
import { explicarFalha } from '@/lib/pedido';
import { retornoDe } from '@/lib/retorno';

import { apiDaTelemetria, useMaquinasParaLigar } from './api';
import {
  FORMATOS,
  INTEGRACAO_VAZIA,
  ROTULO_DO_TIPO,
  TIPOS_DE_MENSAGEM,
  faltaNaIntegracao,
  retornoDaChave,
  retornoDaConferencia,
  retornoDaRevogacao,
  retornoDoDescarte,
  type ChaveDaIntegracao,
  type DadosDaIntegracao,
  type DispositivoCadastrado,
  type Formato,
  type Integracao,
  type MensagemDaFila,
  type Regras,
  type RespostaDaChave,
} from './telemetria';

/**
 * As janelas da telemetria na mesa (p.30): tarefa curta, ao centro, no máximo
 * seis campos. Cada uma é desenhada só por quem tem a licença da gravação dela
 * (a página decide), e cada gravação declara o seu retorno (R04).
 */

function Recusa({ erro }: { readonly erro: unknown }) {
  const f = explicarFalha(erro);
  return (
    <div role="alert" className="flex flex-col gap-1 rounded-controle border border-borda bg-rebaixada p-3">
      <span className="txt-corpo text-tinta">{f.erro}</span>
      <span className="txt-apoio text-tinta-secundaria">{f.comoResolver}</span>
    </div>
  );
}

function useInvalidar() {
  const cliente = useQueryClient();
  return () => {
    void cliente.invalidateQueries({ queryKey: ['telemetria'] });
  };
}

/* ======================================================== a fila */

export function JanelaDeDescarte({ alvo, aoFechar }: { readonly alvo: MensagemDaFila | null; readonly aoFechar: () => void }) {
  const invalidar = useInvalidar();
  const [motivo, setMotivo] = useState('');
  const descartar = useMutation({
    mutationFn: (v: { ids: readonly string[]; motivo: string }) => apiDaTelemetria.descartar(v.ids, v.motivo),
    meta: { retorno: retornoDe<{ descartadas: number }, { motivo: string }>((r, v) => ({ titulo: r.descartadas === 1 ? 'Mensagem descartada' : 'Mensagens descartadas', ...retornoDoDescarte(r, v.motivo) })), silenciarFalha: true },
    onSuccess: () => {
      invalidar();
      fechar();
    },
  });
  function fechar() {
    descartar.reset();
    setMotivo('');
    aoFechar();
  }
  const curto = motivo.trim().length < 3;
  return (
    <Janela
      titulo={alvo ? `Descartar a mensagem ${alvo.idExterno}?` : 'Descartar a mensagem?'}
      descricao="Ela sai da fila e da conta do dia. A trilha guarda quem descartou e por quê; a mensagem fica registrada como veio."
      aberta={alvo !== null}
      aoFechar={fechar}
      acoes={
        <>
          <Botao nivel="terciario" onClick={fechar}>
            Manter na fila
          </Botao>
          <Botao
            nivel="destrutivo"
            disabled={descartar.isPending || curto}
            title={curto ? 'Escreva o motivo para descartar.' : undefined}
            onClick={() => {
              if (alvo) descartar.mutate({ ids: [alvo.id], motivo: motivo.trim() });
            }}
          >
            {descartar.isPending ? 'Descartando…' : 'Descartar mensagem'}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {descartar.isError ? <Recusa erro={descartar.error} /> : null}
        <CampoTexto
          rotulo="Motivo"
          valor={motivo}
          aoMudar={setMotivo}
          placeholder="Leitura do dispositivo com defeito"
          ajuda="Fica na trilha com o seu nome. Pelo menos 3 caracteres."
          maxLength={400}
        />
      </div>
    </Janela>
  );
}

/* ======================================================== a conferência do medidor */

export function JanelaDeConferencia({
  alvo,
  aoFechar,
}: {
  readonly alvo: { readonly vinculoId: string; readonly equipamento: string; readonly medidor: 'horimetro' | 'odometro'; readonly ultimoContador: number | null } | null;
  readonly aoFechar: () => void;
}) {
  const base = useId();
  const invalidar = useInvalidar();
  const [painel, setPainel] = useState('');
  const [dispositivo, setDispositivo] = useState('');
  const [tentou, setTentou] = useState(false);
  const u = alvo?.medidor === 'odometro' ? 'km' : 'h';
  const conferir = useMutation({
    mutationFn: (v: { vinculoId: string; painel: number; dispositivo: number }) => apiDaTelemetria.conferir(v.vinculoId, v.painel, v.dispositivo),
    meta: { retorno: retornoDe<{ equipamento: string; diferenca: number }>((r) => ({ titulo: 'Medidor conferido', ...retornoDaConferencia(r, u) })), silenciarFalha: true },
    onSuccess: () => {
      invalidar();
      fechar();
    },
  });
  function fechar() {
    conferir.reset();
    setPainel('');
    setDispositivo('');
    setTentou(false);
    aoFechar();
  }
  const nPainel = interpretarNumero(painel);
  const nDispositivo = interpretarNumero(dispositivo || (alvo?.ultimoContador === null || alvo?.ultimoContador === undefined ? '' : String(alvo.ultimoContador)));
  return (
    <Janela
      titulo={alvo ? `Conferir o medidor de ${alvo.equipamento}` : 'Conferir o medidor'}
      descricao="Leia o painel da máquina e escreva ao lado da leitura que o dispositivo mandou no mesmo momento. A diferença é somada a toda leitura da telemetria; a partir daqui, a última leitura de cada dia fechado vira leitura da máquina."
      aberta={alvo !== null}
      aoFechar={fechar}
      acoes={
        <>
          <Botao nivel="terciario" onClick={fechar}>
            Cancelar
          </Botao>
          <Botao
            nivel="primario"
            disabled={conferir.isPending}
            onClick={() => {
              setTentou(true);
              if (!alvo || nPainel === null || nDispositivo === null) return;
              conferir.mutate({ vinculoId: alvo.vinculoId, painel: nPainel, dispositivo: nDispositivo });
            }}
          >
            {conferir.isPending ? 'Conferindo…' : 'Conferir medidor'}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {conferir.isError ? <Recusa erro={conferir.error} /> : null}
        <Campo id={`${base}-painel`} rotulo="Leitura do painel" erro={tentou && nPainel === null ? `Informe o que o painel mostra, em ${u}.` : null} ajuda="O número que está no painel da máquina agora.">
          <input id={`${base}-painel`} inputMode="decimal" className="controle tabular-nums" value={painel} placeholder="4.832,5" onChange={(e) => { setPainel(e.target.value); }} />
        </Campo>
        <Campo
          id={`${base}-dispositivo`}
          rotulo="Leitura do dispositivo"
          erro={tentou && nDispositivo === null ? `Informe a leitura do dispositivo, em ${u}.` : null}
          ajuda={alvo?.ultimoContador !== null && alvo?.ultimoContador !== undefined ? `A última que chegou foi ${formatarDecimal(alvo.ultimoContador, 1)} ${u}.` : 'Nenhuma leitura chegou ainda deste dispositivo.'}
        >
          <input
            id={`${base}-dispositivo`}
            inputMode="decimal"
            className="controle tabular-nums"
            value={dispositivo}
            placeholder={alvo?.ultimoContador !== null && alvo?.ultimoContador !== undefined ? formatarDecimal(alvo.ultimoContador, 1) : '4.343,5'}
            onChange={(e) => { setDispositivo(e.target.value); }}
          />
        </Campo>
      </div>
    </Janela>
  );
}

/* ======================================================== a integração */

export function JanelaDeIntegracao({
  aberta,
  editando,
  aoFechar,
}: {
  readonly aberta: boolean;
  /** Com a integração, edita os campos, a frequência, a licença e se está ativa; sem, cadastra. */
  readonly editando: Integracao | null;
  readonly aoFechar: () => void;
}) {
  const base = useId();
  const invalidar = useInvalidar();
  const inicial: DadosDaIntegracao = editando
    ? { nome: editando.nome, formato: editando.formato, campos: editando.campos, frequenciaMin: String(editando.frequenciaMin), licencaAte: editando.licencaAte ?? '' }
    : INTEGRACAO_VAZIA;
  const [v, setV] = useState<DadosDaIntegracao>(inicial);
  const [ativa, setAtiva] = useState(editando?.ativa ?? true);
  const [tentou, setTentou] = useState(false);
  const salvar = useMutation({
    mutationFn: (d: DadosDaIntegracao & { readonly ativa: boolean }): Promise<{ readonly nome: string; readonly campos: number }> =>
      editando
        ? apiDaTelemetria.editarIntegracao(editando.id, { campos: d.campos, frequenciaMin: Number(d.frequenciaMin), licencaAte: d.licencaAte || null, ativa: d.ativa })
        : apiDaTelemetria.cadastrarIntegracao({ nome: d.nome, formato: d.formato, campos: d.campos, frequenciaMin: Number(d.frequenciaMin), licencaAte: d.licencaAte || null }),
    meta: {
      retorno: retornoDe<{ nome: string; campos: number }, DadosDaIntegracao & { readonly ativa: boolean }>((r, d) => ({
        titulo: editando ? 'Integração alterada' : 'Integração cadastrada',
        alvo: { nome: r.nome },
        fatos: [`${String(r.campos)} ${r.campos === 1 ? 'campo declarado' : 'campos declarados'}`, `A cada ${d.frequenciaMin} min`, editando && !d.ativa ? 'Desativada' : null],
        efeito: editando ? 'Mensagem de campo que ela não declara fica pendente na fila' : 'Emita a chave e cadastre os dispositivos para o provedor começar a mandar',
      })),
      silenciarFalha: true,
    },
    onSuccess: () => {
      invalidar();
      fechar();
    },
  });
  function fechar() {
    salvar.reset();
    setV(INTEGRACAO_VAZIA);
    setTentou(false);
    aoFechar();
  }
  const falta = tentou ? faltaNaIntegracao(v) : {};
  return (
    <Janela
      titulo={editando ? `Alterar ${editando.nome}` : 'Cadastrar integração'}
      descricao="O provedor de telemetria, ou o rastreador que manda sozinho. Declare só os campos que ele manda de verdade: mensagem de campo não declarado fica pendente."
      aberta={aberta}
      aoFechar={fechar}
      largura="m"
      acoes={
        <>
          <Botao nivel="terciario" onClick={fechar}>
            Cancelar
          </Botao>
          <Botao
            nivel="primario"
            disabled={salvar.isPending}
            onClick={() => {
              setTentou(true);
              if (Object.keys(faltaNaIntegracao(v)).length > 0) return;
              salvar.mutate({ ...v, ativa });
            }}
          >
            {salvar.isPending ? 'Salvando…' : editando ? 'Salvar integração' : 'Cadastrar integração'}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {salvar.isError ? <Recusa erro={salvar.error} /> : null}
        {editando ? null : (
          <>
            <CampoTexto rotulo="Nome" valor={v.nome} aoMudar={(nome) => { setV({ ...v, nome }); }} placeholder="Rastreador da frota" maxLength={80} {...(falta.nome ? { ajuda: falta.nome } : {})} />
            <CampoSelecao
              rotulo="Formato"
              valor={v.formato}
              aoMudar={(formato) => { setV({ ...v, formato: formato as Formato }); }}
              opcoes={Object.entries(FORMATOS).map(([valor, rotulo]) => ({ valor, rotulo }))}
            />
          </>
        )}
        <fieldset className="flex flex-col gap-2">
          <legend className="txt-rotulo-campo pb-1">Campos que ela manda</legend>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 max-[560px]:grid-cols-1">
            {TIPOS_DE_MENSAGEM.map((t) => (
              <label key={t} className="flex min-h-10 items-center gap-2">
                <input
                  type="checkbox"
                  checked={v.campos.includes(t)}
                  onChange={(e) => {
                    setV({ ...v, campos: e.target.checked ? [...v.campos, t] : v.campos.filter((c) => c !== t) });
                  }}
                />
                <span className="txt-corpo text-tinta">{ROTULO_DO_TIPO[t]}</span>
              </label>
            ))}
          </div>
          {falta.campos ? <p className="txt-ajuda text-erro">{falta.campos}</p> : null}
        </fieldset>
        <div className="grid grid-cols-2 gap-4 max-[560px]:grid-cols-1">
          <Campo id={`${base}-freq`} rotulo="Frequência" erro={falta.frequenciaMin ?? null} ajuda="De quanto em quanto tempo ela manda. É a base do sem sinal.">
            <span className="flex items-center gap-2">
              <input id={`${base}-freq`} inputMode="numeric" className="controle w-full tabular-nums" value={v.frequenciaMin} onChange={(e) => { setV({ ...v, frequenciaMin: e.target.value }); }} />
              <span className="txt-corpo text-tinta-apagada">min</span>
            </span>
          </Campo>
          <Campo id={`${base}-licenca`} rotulo="Licença até" opcional ajuda="Até quando o contrato do provedor vale.">
            <input id={`${base}-licenca`} type="date" className="controle" value={v.licencaAte} onChange={(e) => { setV({ ...v, licencaAte: e.target.value }); }} />
          </Campo>
        </div>
        {editando ? (
          <label className="flex min-h-10 items-center gap-2">
            <input type="checkbox" checked={ativa} onChange={(e) => { setAtiva(e.target.checked); }} />
            <span className="txt-corpo text-tinta">Ativa: as chaves dela recebem mensagens</span>
          </label>
        ) : null}
      </div>
    </Janela>
  );
}

/* ======================================================== as chaves */

export function JanelaDaChaveNova({ integracao, aoFechar }: { readonly integracao: Integracao | null; readonly aoFechar: () => void }) {
  const invalidar = useInvalidar();
  const [copiada, setCopiada] = useState(false);
  const emitir = useMutation({
    mutationFn: (id: string) => apiDaTelemetria.emitirChave(id),
    meta: { retorno: retornoDe<RespostaDaChave>((r) => ({ titulo: 'Chave emitida', ...retornoDaChave(r) })), silenciarFalha: true },
    onSuccess: invalidar,
  });
  function fechar() {
    emitir.reset();
    setCopiada(false);
    aoFechar();
  }
  const chave = emitir.data?.chave ?? null;
  return (
    <Janela
      titulo={integracao ? `Emitir chave para ${integracao.nome}` : 'Emitir chave'}
      descricao="A chave aparece uma vez só: o sistema guarda só a impressão dela. Copie e entregue ao provedor; se perder, revogue e emita outra."
      aberta={integracao !== null}
      aoFechar={fechar}
      acoes={
        chave ? (
          <Botao nivel="primario" onClick={fechar}>
            Guardei a chave
          </Botao>
        ) : (
          <>
            <Botao nivel="terciario" onClick={fechar}>
              Cancelar
            </Botao>
            <Botao
              nivel="primario"
              disabled={emitir.isPending}
              onClick={() => {
                if (integracao) emitir.mutate(integracao.id);
              }}
            >
              {emitir.isPending ? 'Emitindo…' : 'Emitir chave'}
            </Botao>
          </>
        )
      }
    >
      <div className="flex flex-col gap-4">
        {emitir.isError ? <Recusa erro={emitir.error} /> : null}
        {chave ? (
          <>
            <code className="mono txt-apoio block rounded-controle bg-rebaixada p-4 text-tinta wrap-anywhere">{chave}</code>
            <div className="flex flex-wrap items-center gap-3">
              <Botao
                nivel="secundario"
                onClick={() => {
                  void navigator.clipboard?.writeText(chave).then(() => {
                    setCopiada(true);
                  });
                }}
              >
                <Copy aria-hidden="true" className="size-[var(--icone-controle)]" />
                {copiada ? 'Chave copiada' : 'Copiar chave'}
              </Botao>
              <span className="txt-apoio text-tinta-secundaria">No cabeçalho de cada envio: Authorization: Bearer seguido da chave.</span>
            </div>
          </>
        ) : (
          <p className="txt-apoio text-pretty text-tinta-secundaria">
            Uma integração pode ter mais de uma chave valendo ao mesmo tempo, para trocar sem parar o envio: emita a nova, troque no provedor e revogue a antiga.
          </p>
        )}
      </div>
    </Janela>
  );
}

export function JanelaDeRevogacao({ chave, aoFechar }: { readonly chave: ChaveDaIntegracao | null; readonly aoFechar: () => void }) {
  const invalidar = useInvalidar();
  const [motivo, setMotivo] = useState('');
  const revogar = useMutation({
    mutationFn: (v: { id: string; motivo: string }) => apiDaTelemetria.revogarChave(v.id, v.motivo),
    meta: { retorno: retornoDe<{ prefixo: string }, { motivo: string }>((r, v) => ({ titulo: 'Chave revogada', ...retornoDaRevogacao(r, v.motivo) })), silenciarFalha: true },
    onSuccess: () => {
      invalidar();
      fechar();
    },
  });
  function fechar() {
    revogar.reset();
    setMotivo('');
    aoFechar();
  }
  const curto = motivo.trim().length < 3;
  return (
    <Janela
      titulo={chave ? `Revogar a chave ${chave.prefixo}?` : 'Revogar a chave?'}
      descricao="Nada mais entra por ela, e ela não volta a valer. As mensagens que já entraram continuam."
      aberta={chave !== null}
      aoFechar={fechar}
      acoes={
        <>
          <Botao nivel="terciario" onClick={fechar}>
            Manter a chave
          </Botao>
          <Botao
            nivel="destrutivo"
            disabled={revogar.isPending || curto}
            title={curto ? 'Escreva o motivo para revogar.' : undefined}
            onClick={() => {
              if (chave) revogar.mutate({ id: chave.id, motivo: motivo.trim() });
            }}
          >
            {revogar.isPending ? 'Revogando…' : 'Revogar chave'}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {revogar.isError ? <Recusa erro={revogar.error} /> : null}
        <CampoTexto rotulo="Motivo" valor={motivo} aoMudar={setMotivo} placeholder="Troca do provedor" ajuda="Fica na trilha com o seu nome. Pelo menos 3 caracteres." maxLength={400} />
      </div>
    </Janela>
  );
}

/* ======================================================== o dispositivo e o vínculo */

export function JanelaDeDispositivo({
  aberta,
  integracoes,
  aoFechar,
}: {
  readonly aberta: boolean;
  readonly integracoes: readonly Integracao[];
  readonly aoFechar: () => void;
}) {
  const invalidar = useInvalidar();
  const [integracaoId, setIntegracaoId] = useState('');
  const [identificador, setIdentificador] = useState('');
  const [descricao, setDescricao] = useState('');
  const [tentou, setTentou] = useState(false);
  const cadastrar = useMutation({
    mutationFn: (d: { integracaoId: string; identificador: string; descricao: string | null }) => apiDaTelemetria.cadastrarDispositivo(d),
    meta: {
      retorno: retornoDe<{ identificador: string; integracao: string }>((r) => ({
        titulo: 'Dispositivo cadastrado',
        alvo: { codigo: r.identificador, nome: r.integracao },
        efeito: 'Ligue-o à máquina para as mensagens dele entrarem',
      })),
      silenciarFalha: true,
    },
    onSuccess: () => {
      invalidar();
      fechar();
    },
  });
  function fechar() {
    cadastrar.reset();
    setIdentificador('');
    setDescricao('');
    setTentou(false);
    aoFechar();
  }
  const escolhida = integracaoId || integracoes[0]?.id || '';
  return (
    <Janela
      titulo="Cadastrar dispositivo"
      descricao="O identificador é o que o provedor manda em cada mensagem: número de série, PIN ou IMEI."
      aberta={aberta}
      aoFechar={fechar}
      acoes={
        <>
          <Botao nivel="terciario" onClick={fechar}>
            Cancelar
          </Botao>
          <Botao
            nivel="primario"
            disabled={cadastrar.isPending}
            onClick={() => {
              setTentou(true);
              if (!escolhida || !identificador.trim()) return;
              cadastrar.mutate({ integracaoId: escolhida, identificador: identificador.trim(), descricao: descricao.trim() || null });
            }}
          >
            {cadastrar.isPending ? 'Cadastrando…' : 'Cadastrar dispositivo'}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {cadastrar.isError ? <Recusa erro={cadastrar.error} /> : null}
        <CampoSelecao rotulo="Integração" valor={escolhida} aoMudar={setIntegracaoId} opcoes={integracoes.map((i) => ({ valor: i.id, rotulo: i.nome }))} />
        <CampoTexto
          rotulo="Identificador"
          valor={identificador}
          aoMudar={setIdentificador}
          placeholder="SN-4471"
          maxLength={80}
          {...(tentou && !identificador.trim() ? { ajuda: 'Informe o identificador como o provedor manda.' } : {})}
        />
        <CampoTexto rotulo="Descrição" opcional valor={descricao} aoMudar={setDescricao} placeholder="Rastreador da cabine" maxLength={120} />
      </div>
    </Janela>
  );
}

export function JanelaDeLigar({ dispositivo, aoFechar }: { readonly dispositivo: DispositivoCadastrado | null; readonly aoFechar: () => void }) {
  const base = useId();
  const invalidar = useInvalidar();
  const [termo, setTermo] = useState('');
  const [equipamentoId, setEquipamentoId] = useState('');
  const [inicio, setInicio] = useState(paraCampoDataHora(new Date()));
  const [tentou, setTentou] = useState(false);
  const maquinas = useMaquinasParaLigar(termo.trim(), dispositivo !== null);
  const ligar = useMutation({
    mutationFn: (d: { dispositivoId: string; equipamentoId: string; inicio: string }) => apiDaTelemetria.ligar(d),
    meta: {
      retorno: retornoDe<{ identificador: string; equipamento: string }>((r) => ({
        titulo: 'Dispositivo ligado à máquina',
        alvo: { codigo: r.equipamento, nome: r.identificador },
        efeito: 'As mensagens a partir do início entram nesta máquina. Confira o medidor para elas alimentarem o horímetro',
      })),
      silenciarFalha: true,
    },
    onSuccess: () => {
      invalidar();
      fechar();
    },
  });
  function fechar() {
    ligar.reset();
    setTermo('');
    setEquipamentoId('');
    setInicio(paraCampoDataHora(new Date()));
    setTentou(false);
    aoFechar();
  }
  const lista = maquinas.data?.maquinas ?? [];
  return (
    <Janela
      titulo={dispositivo ? `Ligar ${dispositivo.identificador} a uma máquina` : 'Ligar a uma máquina'}
      descricao="Um dispositivo fica numa máquina só de cada vez. O vínculo vale a partir do início; o histórico de antes fica."
      aberta={dispositivo !== null}
      aoFechar={fechar}
      acoes={
        <>
          <Botao nivel="terciario" onClick={fechar}>
            Cancelar
          </Botao>
          <Botao
            nivel="primario"
            disabled={ligar.isPending}
            onClick={() => {
              setTentou(true);
              if (!dispositivo || !equipamentoId || !inicio) return;
              ligar.mutate({ dispositivoId: dispositivo.id, equipamentoId, inicio: new Date(inicio).toISOString() });
            }}
          >
            {ligar.isPending ? 'Ligando…' : 'Ligar à máquina'}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {ligar.isError ? <Recusa erro={ligar.error} /> : null}
        <CampoTexto rotulo="Buscar máquina" valor={termo} aoMudar={setTermo} placeholder="ESC-001" ajuda="Mostra até 30. Escreva parte do código ou da descrição." />
        <div role="radiogroup" aria-label="Máquinas encontradas" className="flex max-h-56 flex-col overflow-auto rounded-controle border border-borda">
          {maquinas.isPending ? (
            <span className="esqueleto m-3 block h-10" aria-hidden="true" />
          ) : maquinas.isError ? (
            <p role="alert" className="txt-apoio p-3 text-erro-texto">{explicarFalha(maquinas.error).comoResolver}</p>
          ) : lista.length === 0 ? (
            <p className="txt-apoio p-3 text-tinta-secundaria">{termo.trim() ? `Nenhuma máquina com “${termo.trim()}”. Tente outra parte do código.` : 'Nenhuma máquina ativa.'}</p>
          ) : (
            lista.map((m) => (
              <button
                key={m.id}
                type="button"
                role="radio"
                aria-checked={equipamentoId === m.id}
                onClick={() => { setEquipamentoId(m.id); }}
                className={`flex min-h-12 flex-col justify-center border-b border-borda-sutil px-3 py-2 text-left last:border-b-0 ${equipamentoId === m.id ? 'bg-marca-fundo' : ''}`}
              >
                <span className="codigo text-tinta">{m.codigo}</span>
                <span className="txt-apoio truncate text-tinta-secundaria">{m.descricao}</span>
              </button>
            ))
          )}
        </div>
        {tentou && !equipamentoId ? <p className="txt-ajuda text-erro">Escolha a máquina na lista.</p> : null}
        <Campo id={`${base}-inicio`} rotulo="Início" ajuda="Desde quando o dispositivo está nesta máquina. As mensagens de antes ficam pendentes.">
          <input id={`${base}-inicio`} type="datetime-local" className="controle" value={inicio} onChange={(e) => { setInicio(e.target.value); }} />
        </Campo>
      </div>
    </Janela>
  );
}

export function JanelaDeEncerrar({ dispositivo, aoFechar }: { readonly dispositivo: DispositivoCadastrado | null; readonly aoFechar: () => void }) {
  const base = useId();
  const invalidar = useInvalidar();
  const [fim, setFim] = useState(paraCampoDataHora(new Date()));
  const encerrar = useMutation({
    mutationFn: (d: { vinculoId: string; fim: string }) => apiDaTelemetria.encerrar(d.vinculoId, d.fim),
    meta: {
      retorno: retornoDe<{ identificador: string; equipamento: string }>((r) => ({
        titulo: 'Vínculo encerrado',
        alvo: { codigo: r.equipamento, nome: r.identificador },
        efeito: 'Mensagens depois do fim ficam pendentes até o dispositivo ser ligado de novo',
      })),
      silenciarFalha: true,
    },
    onSuccess: () => {
      invalidar();
      fechar();
    },
  });
  function fechar() {
    encerrar.reset();
    setFim(paraCampoDataHora(new Date()));
    aoFechar();
  }
  return (
    <Janela
      titulo={dispositivo ? `Tirar ${dispositivo.identificador} de ${dispositivo.equipamento ?? 'máquina'}?` : 'Encerrar o vínculo?'}
      descricao="O vínculo fecha no fim escolhido; o que entrou até lá continua na máquina. Nada é apagado."
      aberta={dispositivo !== null}
      aoFechar={fechar}
      acoes={
        <>
          <Botao nivel="terciario" onClick={fechar}>
            Manter na máquina
          </Botao>
          <Botao
            nivel="primario"
            disabled={encerrar.isPending}
            onClick={() => {
              if (dispositivo?.vinculoId && fim) encerrar.mutate({ vinculoId: dispositivo.vinculoId, fim: new Date(fim).toISOString() });
            }}
          >
            {encerrar.isPending ? 'Encerrando…' : 'Encerrar vínculo'}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {encerrar.isError ? <Recusa erro={encerrar.error} /> : null}
        <Campo id={`${base}-fim`} rotulo="Fim" ajuda="Quando o dispositivo saiu da máquina.">
          <input id={`${base}-fim`} type="datetime-local" className="controle" value={fim} onChange={(e) => { setFim(e.target.value); }} />
        </Campo>
      </div>
    </Janela>
  );
}

/* ======================================================== as regras */

export function JanelaDasRegras({ aberta, regras, aoFechar }: { readonly aberta: boolean; readonly regras: Regras; readonly aoFechar: () => void }) {
  const base = useId();
  const invalidar = useInvalidar();
  const [semSinal, setSemSinal] = useState(String(regras.semSinalVezes).replace('.', ','));
  const [ociosidade, setOciosidade] = useState(String(regras.ociosidadePct).replace('.', ','));
  const [consumo, setConsumo] = useState(String(regras.toleranciaConsumoPct).replace('.', ','));
  const [motivo, setMotivo] = useState('');
  const [tentou, setTentou] = useState(false);
  const salvar = useMutation({
    mutationFn: (d: { semSinalVezes: number; ociosidadePct: number; toleranciaConsumoPct: number; motivo: string }) => apiDaTelemetria.regras(d),
    meta: {
      retorno: retornoDe<{ semSinalVezes: number; ociosidadePct: number; toleranciaConsumoPct: number }>((r) => ({
        titulo: 'Regras da telemetria alteradas',
        fatos: [
          `Sem sinal depois de ${formatarDecimal(r.semSinalVezes, 1)} vezes a frequência`,
          `Ociosidade a partir de ${formatarDecimal(r.ociosidadePct, 1)}%`,
          `Consumo com tolerância de ${formatarDecimal(r.toleranciaConsumoPct, 1)}%`,
        ],
        efeito: 'Os avisos e a conciliação passam a usar as regras novas',
      })),
      silenciarFalha: true,
    },
    onSuccess: () => {
      invalidar();
      fechar();
    },
  });
  function fechar() {
    salvar.reset();
    setMotivo('');
    setTentou(false);
    aoFechar();
  }
  const s = interpretarNumero(semSinal);
  const o = interpretarNumero(ociosidade);
  const c = interpretarNumero(consumo);
  return (
    <Janela
      titulo="Alterar as regras da telemetria"
      descricao="Valem para a empresa inteira. Sem regra salva, valem as recomendadas: 3 vezes a frequência, 30% e 10%."
      aberta={aberta}
      aoFechar={fechar}
      acoes={
        <>
          <Botao nivel="terciario" onClick={fechar}>
            Cancelar
          </Botao>
          <Botao
            nivel="primario"
            disabled={salvar.isPending}
            onClick={() => {
              setTentou(true);
              if (s === null || o === null || c === null || motivo.trim().length < 3) return;
              salvar.mutate({ semSinalVezes: s, ociosidadePct: o, toleranciaConsumoPct: c, motivo: motivo.trim() });
            }}
          >
            {salvar.isPending ? 'Salvando…' : 'Salvar regras'}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {salvar.isError ? <Recusa erro={salvar.error} /> : null}
        <Campo id={`${base}-sinal`} rotulo="Sem sinal depois de" erro={tentou && s === null ? 'Informe um número de 1 a 48.' : null} ajuda="Vezes a frequência da integração, no mínimo 30 minutos.">
          <span className="flex items-center gap-2">
            <input id={`${base}-sinal`} inputMode="decimal" className="controle w-full tabular-nums" value={semSinal} onChange={(e) => { setSemSinal(e.target.value); }} />
            <span className="txt-corpo text-tinta-apagada">vezes</span>
          </span>
        </Campo>
        <Campo id={`${base}-ociosidade`} rotulo="Ociosidade que avisa" erro={tentou && o === null ? 'Informe de 1 a 100.' : null} ajuda="A parte das horas de motor do dia em marcha lenta.">
          <span className="flex items-center gap-2">
            <input id={`${base}-ociosidade`} inputMode="decimal" className="controle w-full tabular-nums" value={ociosidade} onChange={(e) => { setOciosidade(e.target.value); }} />
            <span className="txt-corpo text-tinta-apagada">%</span>
          </span>
        </Campo>
        <Campo id={`${base}-consumo`} rotulo="Tolerância do consumo" erro={tentou && c === null ? 'Informe de 0 a 100.' : null} ajuda="A diferença entre o abastecido e o telemetrado que chama atenção.">
          <span className="flex items-center gap-2">
            <input id={`${base}-consumo`} inputMode="decimal" className="controle w-full tabular-nums" value={consumo} onChange={(e) => { setConsumo(e.target.value); }} />
            <span className="txt-corpo text-tinta-apagada">%</span>
          </span>
        </Campo>
        <CampoTexto rotulo="Motivo" valor={motivo} aoMudar={setMotivo} placeholder="Provedor manda a cada 5 minutos" maxLength={400} ajuda={tentou && motivo.trim().length < 3 ? 'Diga por que as regras mudam, em pelo menos 3 caracteres.' : 'Fica na trilha com o seu nome.'} />
      </div>
    </Janela>
  );
}
